import * as React from 'react'
import { DocumentRecord } from '@logic'
import {
  TableContainer,
  StyledTable,
  DocumentName,
  CategoryBadge,
  ActionsCell,
  EmptyState,
} from '../style'
import { getCategoryLabel, AgreementCategory } from '../utils'
import { Ellipses } from '@components/tables/ellipses'
import FileIcon from '@image/file.svg'
import FolderIcon from '@image/folder-icon.svg'
import { formatBytes, formatDateString } from '@helpers/util'

interface AgreementsTableProps {
  documents: DocumentRecord[]
  category: AgreementCategory
  onView: (doc: DocumentRecord) => void
  onDelete: (doc: DocumentRecord) => void
  onFolderClick: (doc: DocumentRecord) => void
  canEdit: boolean
  canDelete: boolean
  isLegacyDataRoom: boolean
}

export const AgreementsTable: React.FC<AgreementsTableProps> = ({
  documents,
  category,
  onView,
  onDelete,
  onFolderClick,
  canEdit,
  canDelete,
  isLegacyDataRoom,
}) => {
  if (!documents || documents.length === 0) {
    return (
      <EmptyState>
        <FileIcon />
        <h3>No documents found</h3>
        <p>
          {category === AgreementCategory.all
            ? 'Upload your first governing document to get started.'
            : `No documents in the ${getCategoryLabel(category)} category yet.`}
        </p>
      </EmptyState>
    )
  }

  const isFolder = (doc: DocumentRecord): boolean => {
    return isLegacyDataRoom ? !!doc.folder : doc.type === 2
  }

  const getActionOptions = (doc: DocumentRecord): Array<[string, () => void] | false> => {
    const docIsFolder = isFolder(doc)
    return [
      !docIsFolder && ['View', () => onView(doc)],
      canDelete && ['Delete', () => onDelete(doc)],
    ]
  }

  const getNameWithoutExtension = (name: string, docIsFolder: boolean): string => {
    if (docIsFolder) return name
    return name.includes('.')
      ? name.split('.').slice(0, -1).join('.')
      : name
  }

  return (
    <TableContainer>
      <StyledTable>
        <thead>
          <tr>
            <th>Document Name</th>
            {category === AgreementCategory.all && <th>Category</th>}
            <th>Date Uploaded</th>
            <th>File Size</th>
            <th style={{ textAlign: 'right' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {documents.map((doc, index) => {
            const docIsFolder = isFolder(doc)
            const name = doc.name || 'Unknown'
            const displayName = getNameWithoutExtension(name, docIsFolder)
            const date = (doc as any).createdDateTime
            const size = (doc as any).size

            return (
              <tr key={doc.id || index}>
                <td>
                  <DocumentName
                    onClick={() => (docIsFolder ? onFolderClick(doc) : onView(doc))}
                  >
                    {docIsFolder ? <FolderIcon /> : <FileIcon />}
                    {displayName}
                  </DocumentName>
                </td>
                {category === AgreementCategory.all && (
                  <td>
                    <CategoryBadge>{getCategoryLabel(category)}</CategoryBadge>
                  </td>
                )}
                <td>{date ? formatDateString(date) : '-'}</td>
                <td>{!docIsFolder && size ? formatBytes(size) : '-'}</td>
                <td>
                  <ActionsCell>
                    {!docIsFolder && <Ellipses options={getActionOptions(doc)} table={true} />}
                  </ActionsCell>
                </td>
              </tr>
            )
          })}
        </tbody>
      </StyledTable>
    </TableContainer>
  )
}
